import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import { Building2, DollarSign, TrendingUp, CheckCircle, Clock, AlertCircle } from 'lucide-react';

export const Dashboard = ({ currentUser, globalSearch }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [commissionConfig, setCommissionConfig] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const userName = currentUser?.name;
      const userId = currentUser?.id;
      
      // Load all data in parallel including commission config
      const results = await Promise.allSettled([
        api.getStats(userName),
        api.getDeals(),
        api.getEvaluaciones(),
        api.getPipelines(),
        userId ? api.getUserCommissions(userId) : Promise.resolve(null)
      ]);

      const [statsRes, dealsRes, evalRes, pipelinesRes, commissionRes] = results;

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value?.data || statsRes.value || {});
      }

      // Load commission config
      if (commissionRes.status === 'fulfilled' && commissionRes.value) {
        const commConfig = commissionRes.value?.data || commissionRes.value;
        setCommissionConfig(commConfig);
      }

      let pipelinesData = [];
      if (pipelinesRes.status === 'fulfilled') {
        pipelinesData = pipelinesRes.value?.data || pipelinesRes.value || [];
        setPipelines(pipelinesData);
      }

      // Create pipeline map
      const pipelineMap = {};
      pipelinesData.forEach(p => {
        if (p.id) pipelineMap[p.id] = p.name || 'Sin Pipeline';
      });

      if (dealsRes.status === 'fulfilled') {
        const dealsData = dealsRes.value?.data || dealsRes.value || [];
        // Enrich deals with pipeline names and filter by user
        const enrichedDeals = (Array.isArray(dealsData) ? dealsData : [])
          .filter(d => !userName || d.assigned_to === userName || d.owner === userName)
          .map(d => ({
            ...d,
            pipeline_name: d.pipeline_name || pipelineMap[d.pipeline_id] || 'Sin Pipeline'
          }));
        setDeals(enrichedDeals);
      }

      if (evalRes.status === 'fulfilled') {
        const evalData = evalRes.value?.data || evalRes.value || [];
        // Filter evaluaciones by user
        const filteredEvals = (Array.isArray(evalData) ? evalData : [])
          .filter(e => !userName || e.assigned_to === userName || e.sold_by === userName || e.user === userName);
        setEvaluaciones(filteredEvals);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get commission rate for a pipeline
  const getPipelineCommissionRate = (pipelineId) => {
    if (!commissionConfig) return currentUser?.commission_rate || 35;
    const pipelineRate = (commissionConfig.pipeline_rates || []).find(pr => pr.pipeline_id === pipelineId);
    return pipelineRate ? pipelineRate.rate : (commissionConfig.default_rate || 35);
  };

  // Helper function to get commission rate for a service
  const getServiceCommissionRate = (serviceName) => {
    if (!commissionConfig) return currentUser?.commission_rate || 20;
    const serviceRate = (commissionConfig.service_rates || []).find(
      sr => sr.service_name?.toLowerCase() === serviceName?.toLowerCase()
    );
    return serviceRate ? serviceRate.rate : (commissionConfig.default_rate || 20);
  };

  // Calculate metrics from real data
  const empresasCount = stats?.empresas_count || stats?.total_empresas || 0;
  const wonCommissions = stats?.won_commissions || 0;
  const pipelineValue = stats?.pipeline_value || 0;
  const tasksPending = stats?.tasks_pending || 0;
  const tasksCompleted = stats?.tasks_completed || 0;

  // Calculate potential commissions from deals using custom rates
  const dealsCommission = deals.reduce((sum, d) => {
    if (!d.is_won && !d.is_lost) {
      const rate = getPipelineCommissionRate(d.pipeline_id);
      return sum + ((d.value || 0) * (rate / 100));
    }
    return sum;
  }, 0);

  const evalCommission = evaluaciones.reduce((sum, e) => {
    const rate = getServiceCommissionRate(e.service_name);
    return sum + (e.sales_commission || e.potential_commission || ((e.appraisal_value || 0) * (rate / 100)));
  }, 0);

  const totalPotentialCommission = dealsCommission + evalCommission;

  // Calculate recurring revenue from deals
  const recurringRevenue = deals.reduce((sum, d) => sum + (d.monthly_value || 0), 0);

  // Build commission breakdown table from deals (with pipeline names and custom rates)
  const commissionBreakdown = deals
    .filter(d => !d.is_won && !d.is_lost && d.value > 0)
    .map(d => {
      const rate = getPipelineCommissionRate(d.pipeline_id);
      return {
        id: d.id,
        client: d.empresa_name || 'Sin empresa',
        service: d.pipeline_name || 'Sin Pipeline',
        appraisal: d.value || 0,
        frequency: d.monthly_value > 0 ? 'Recurrente' : 'One Time Deal',
        monthlyValue: d.monthly_value || 0,
        potentialCommission: (d.value || 0) * (rate / 100),
        commissionRate: rate
      };
    });

  // Add evaluaciones to breakdown with custom rates
  const evalBreakdown = evaluaciones
    .filter(e => e.status !== 'cerrada' && e.status !== 'vendida')
    .map(e => {
      const rate = getServiceCommissionRate(e.service_name);
      return {
        id: e.id,
        client: e.empresa_name || e.client_name || 'Sin cliente',
        service: e.service_name || e.quote_name || 'Cotización',
        appraisal: e.appraisal_value || 0,
        frequency: 'One Time Deal',
        monthlyValue: 0,
        potentialCommission: e.sales_commission || e.potential_commission || ((e.appraisal_value || 0) * (rate / 100)),
        commissionRate: rate
      };
    });

  const allBreakdown = [...commissionBreakdown, ...evalBreakdown];

  const statsCards = [
    { 
      title: 'Mis Empresas', 
      value: empresasCount.toString(), 
      change: 'Asignadas a mí', 
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    { 
      title: 'Comisiones Ganadas', 
      value: `$${wonCommissions.toLocaleString()}`, 
      change: `Este mes (${commissionConfig?.default_rate || currentUser?.commission_rate || 35}%)`, 
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    { 
      title: 'Comisiones Potenciales', 
      value: `$${totalPotentialCommission.toLocaleString()}`, 
      change: `Embudos: $${dealsCommission.toLocaleString()} | Evaluaciones: $${evalCommission.toLocaleString()}`, 
      icon: TrendingUp,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    { 
      title: 'Valor del Pipeline', 
      value: `$${pipelineValue.toLocaleString()}`, 
      change: 'Total oportunidades', 
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    { 
      title: 'Tareas Pendientes', 
      value: tasksPending.toString(), 
      change: '', 
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    { 
      title: 'Tareas Completadas', 
      value: tasksCompleted.toString(), 
      change: '', 
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
  ];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="dashboard-content">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center border-b border-gray-200 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Panel de Control</h1>
            <p className="text-sm text-gray-500 mt-1">Bienvenido, {currentUser?.name || 'Usuario'}</p>
          </div>
        </div>

        {/* Tarjetas de Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-stats">
          {statsCards.map((stat, index) => (
            <div key={stat.title} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between min-h-[140px]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">{stat.title}</span>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon size={20} className={stat.color} />
                </div>
              </div>
              <div>
                <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                {stat.change && (
                  <span className="block text-sm text-gray-400 mt-1">{stat.change}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Tabla de Desglose de Comisiones Potenciales */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Desglose de Comisiones Potenciales</h2>
            <span className="text-sm text-gray-500">{allBreakdown.length} items activos</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm font-medium text-gray-500">
                  <th className="px-6 py-3">Cliente</th>
                  <th className="px-6 py-3">Embudo/Servicio</th>
                  <th className="px-6 py-3">Valor</th>
                  <th className="px-6 py-3">Frecuencia</th>
                  <th className="px-6 py-3">Ingreso Recurrente</th>
                  <th className="px-6 py-3">Tasa</th>
                  <th className="px-6 py-3">Comisión Potencial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allBreakdown.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No hay datos de comisiones potenciales
                    </td>
                  </tr>
                ) : (
                  allBreakdown.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900">{row.client}</td>
                      <td className="px-6 py-4 text-gray-600">{row.service}</td>
                      <td className="px-6 py-4 text-gray-600">${row.appraisal.toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                          row.frequency === 'Recurrente' 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {row.frequency}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-purple-600">
                        {row.monthlyValue > 0 ? `$${row.monthlyValue.toLocaleString()}/mes` : '$0'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          {row.commissionRate}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-green-600 font-medium">${row.potentialCommission.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
